import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PlayerApi } from "./api";
import {
  loadState, saveState,
  loadCachedPlaylist, saveCachedPlaylist,
  estimateCacheSize,
} from "./storage";
import {
  resolveMediaUrl, prefetchAll, evictUnreferenced, registerServiceWorker,
  clearAll as clearMediaCache,
} from "./cache";
import {
  buildSteps, indexLayouts, stepDurationMs, effectiveTransition,
  effectiveTransitionMs, sceneSwitchDelayMs, PlayerStep,
} from "./playbackEngine";
import { buildTransitionPlan } from "./transitionEngine";
import { TransitionStage } from "./TransitionStage";
import LayoutRenderer from "./LayoutRenderer";
import { detectDeviceMeta, toHeartbeatMeta } from "./deviceMeta";
import Diagnostics, { DiagnosticsInfo } from "./Diagnostics";
import {
  IPlayerCommand, IPlayerErrorResponse, IPlayerLastCommand, IPlayerLayout,
  IPlayerLocalState, IPlayerMedia, IPlayerPlaylistResponse,
} from "./types";
import "./player.css";

const APP_VERSION = "web-1.0.0";
const POLL_MS = 60_000;
const CURSOR_HIDE_MS = 5_000;

type PlayerScreen =
  | { kind: "boot" }
  | { kind: "waiting" }
  // Device credential is valid but the backend is gating content delivery
  // (gateContentAccess 403). `reason` is the server's stable machine reason
  // (device_disabled / device_suspended / license_* / trial_expired /
  // subscription_* …) which maps to a specific, non-misleading message —
  // NOT a blanket "Device Disabled". See reasonScreen().
  | { kind: "blocked"; reason: string | null }
  | { kind: "unauthorized" }
  | { kind: "invalid_code" }
  | { kind: "playing" };

/**
 * Map a backend gateContentAccess `reason` to a non-technical title +
 * subtitle. Keeps the distinction the backend already makes rather than
 * collapsing every 403 into "Device Disabled". `device_revoked` /
 * `unknown_device` are handled earlier as a dead credential (Not Authorized /
 * re-pair) and never reach here.
 */
function reasonScreen(reason: string | null): { title: string; subtitle?: string } {
  switch (reason) {
    case "device_disabled":
      return { title: "Device Disabled", subtitle: "This screen has been disabled by an administrator." };
    case "device_suspended":
      return { title: "Device Suspended", subtitle: "This screen is suspended. Contact your administrator." };
    case "device_pending_activation":
      return { title: "Device Not Activated", subtitle: "This screen is awaiting activation." };
    case "license_suspended":
    case "license_expired":
      return { title: "License Inactive", subtitle: "This screen's license is not active. Contact your administrator." };
    case "trial_expired":
      return { title: "Trial Expired", subtitle: "This account's trial has ended. Contact your administrator to continue." };
    case "subscription_suspended":
    case "subscription_cancelled":
    case "subscription_expired":
      return { title: "Subscription Inactive", subtitle: "This account's subscription is not active. Contact your administrator." };
    default:
      return { title: "Content Unavailable", subtitle: "This screen can't play content right now. Contact your administrator." };
  }
}

export interface PlayerProps {
  /**
   * When set, the player plays THIS device instead of reading the code
   * from the route params. Used by the bare `/player` self-pairing flow
   * ({@link ./PlayerEntry}) once a credential has been obtained.
   */
  deviceCodeOverride?: string;
  /**
   * Invoked when the device credential is permanently dead (admin revoked
   * or deleted the device, or the stored code is no longer provisioned).
   * The bare `/player` flow uses this to drop the paired pointer and
   * return to the pairing screen. Undefined on the `/player/:deviceCode`
   * route, where the player simply shows a terminal "not authorized"
   * screen instead.
   */
  onCredentialInvalid?: () => void;
}

/**
 * A single self-resolving media scene. Resolves its own cached URL so the
 * outgoing layer of a transition keeps its already-resolved src (no
 * re-fetch, no black frame) while the incoming layer resolves in parallel.
 * onEnded/onError bubble up so the parent can post events + advance.
 */
const MediaScene: React.FC<{
  media: IPlayerMedia;
  onEnded: () => void;
  onError: () => void;
  /** Transition-into-next duration (ms). When >0 the video advances this
   *  long before its natural end so the transition finishes at the boundary
   *  (spec §8 — item duration is the total active time). */
  nearEndMs?: number;
  onNearEnd?: () => void;
}> = ({ media, onEnded, onError, nearEndMs = 0, onNearEnd }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const s = await resolveMediaUrl(media);
      if (!cancelled) setSrc(s);
    })();
    return () => { cancelled = true; };
  }, [media]);

  if (!src) {
    return <div style={{ width: "100%", height: "100%", background: "#000" }} />;
  }
  if (media.type === "image") {
    return <img className="vmd-player-scene" src={src} alt="" onError={onError} />;
  }
  return (
    <video
      className="vmd-player-scene"
      src={src}
      autoPlay
      muted
      playsInline
      {...({ "webkit-playsinline": "true" } as Record<string, string>)}
      onEnded={onEnded}
      onError={onError}
      onTimeUpdate={(e) => {
        if (!onNearEnd || nearEndMs <= 0) return;
        const el = e.currentTarget;
        if (!el.duration || !isFinite(el.duration)) return;
        if ((el.duration - el.currentTime) * 1000 <= nearEndMs) onNearEnd();
      }}
    />
  );
};

const Player: React.FC<PlayerProps> = ({ deviceCodeOverride, onCredentialInvalid }) => {
  const { deviceCode: paramDeviceCode = "" } = useParams<{ deviceCode: string }>();
  // A non-empty override (bare /player paired flow) always wins over the
  // route param; otherwise fall back to /player/:deviceCode.
  const deviceCode = deviceCodeOverride || paramDeviceCode;
  const [screen, setScreen] = useState<PlayerScreen>({ kind: "boot" });
  const [state, setState] = useState<IPlayerLocalState>(() => loadState(deviceCode));
  const [sequence, setSequence] = useState<PlayerStep[]>([]);
  const [layouts, setLayouts] = useState<Record<number, IPlayerLayout>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagInfo, setDiagInfo] = useState<DiagnosticsInfo | null>(null);
  const [cursorVisible, setCursorVisible] = useState(true);

  // Refs for values touched by interval callbacks — avoids re-creating
  // the intervals on every render.
  const stateRef = useRef(state);
  const sequenceRef = useRef(sequence);
  const indexRef = useRef(currentIndex);
  const screenRef = useRef(screen);
  const onCredentialInvalidRef = useRef(onCredentialInvalid);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { sequenceRef.current = sequence; }, [sequence]);
  useEffect(() => { indexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { screenRef.current = screen; }, [screen]);
  useEffect(() => { onCredentialInvalidRef.current = onCredentialInvalid; }, [onCredentialInvalid]);

  /* ─── State helpers ───────────────────────────────────────────────── */

  const persist = useCallback((next: Partial<IPlayerLocalState>) => {
    setState((prev) => {
      const merged = { ...prev, ...next };
      saveState(merged);
      return merged;
    });
  }, []);

  /**
   * Central handler for a rejected player request. Distinguishes a dead
   * credential (admin revoked/deleted the device, or a 401 token
   * mismatch) from a device that still exists but is temporarily denied
   * content (disabled / license / subscription). A dead credential in
   * the bare `/player` flow drops the pairing pointer and returns to the
   * pairing screen via onCredentialInvalid; everything else shows the
   * appropriate holding screen without discarding the pairing.
   *
   * Returns true when the caller should stop (auth was handled).
   */
  const handleAuthFailure = useCallback((
    status: number,
    data: IPlayerErrorResponse | null,
  ): boolean => {
    if (status !== 401 && status !== 403) return false;
    const reason = data?.reason;
    const credentialDead =
      status === 401 || reason === "device_revoked" || reason === "unknown_device";

    if (credentialDead) {
      if (onCredentialInvalidRef.current) {
        // Paired (bare /player) flow — forget this device and re-pair.
        setScreen({ kind: "unauthorized" });
        persist({ token: null });
        onCredentialInvalidRef.current();
      } else if (status === 401) {
        // /player/:deviceCode flow — token rotated server-side. Drop it so
        // the next boot re-registers against the (still valid) code.
        persist({ token: null });
      } else {
        // Revoked/unknown on the code-URL flow — terminal, non-technical.
        setScreen({ kind: "unauthorized" });
      }
      return true;
    }

    // 403 where the credential is valid but content is gated — surface the
    // SPECIFIC reason (device disabled vs. trial/subscription/license) instead
    // of a blanket "Device Disabled" that misreads a billing state as a dead
    // screen.
    setScreen({ kind: "blocked", reason: reason ?? null });
    return true;
  }, [persist]);

  /* ─── Apply a fresh playlist payload from the API ─────────────────── */

  const applyPlaylist = useCallback(async (payload: IPlayerPlaylistResponse) => {
    if (!payload.playlist_group) {
      setSequence([]);
      setLayouts({});
      setCurrentIndex(0);
      setScreen({ kind: "waiting" });
      persist({
        playlist_version: payload.version || 0,
        last_sync: new Date().toISOString(),
        cached_playlist: payload,
      });
      await saveCachedPlaylist(deviceCode, payload);
      return;
    }
    const seq = buildSteps(payload.playlist_group);
    const layoutMap = indexLayouts(payload);
    setSequence(seq);
    setLayouts(layoutMap);
    setCurrentIndex(0);
    setScreen({ kind: "playing" });
    persist({
      playlist_version: payload.version || 0,
      last_sync: new Date().toISOString(),
      cached_playlist: payload,
    });
    await saveCachedPlaylist(deviceCode, payload);

    // Breadcrumb so the admin can see in the events log when content
    // last reloaded and how many items it contains.
    const token = stateRef.current.token;
    if (token) void PlayerApi.event(token, "playlist_loaded",
      `Loaded ${seq.length} step(s), version ${payload.version}`,
      { item_count: seq.length, version: payload.version });

    // Fire-and-forget: prefetch media to IDB; evict items no longer
    // referenced. V2 — also include media that template image/video
    // widgets reference so offline playback works for templates too.
    void (async () => {
      const mediaSteps = seq.flatMap(s => s.kind === "media" ? [s.media] : []);
      const layoutMedia: IPlayerMedia[] = [];
      for (const layout of Object.values(layoutMap)) {
        for (const z of layout.zones ?? []) {
          if (z.widget_type !== "image" && z.widget_type !== "video") continue;
          const m = (z.widget_config as any)?.media;
          if (m && m.url) {
            layoutMedia.push({
              id: m.id, type: m.type, url: m.url, duration: m.duration,
              sort_order: 0, checksum: m.checksum, file_size: m.file_size,
            });
          }
        }
      }
      const allMedia = [...mediaSteps, ...layoutMedia];
      const active = new Set(allMedia.map(m => m.checksum).filter(Boolean) as string[]);
      await prefetchAll(allMedia);
      await evictUnreferenced(active);
    })();
  }, [deviceCode, persist]);

  /* ─── Boot ─────────────────────────────────────────────────────────── */

  const fetchFreshPlaylist = useCallback(async (token: string): Promise<boolean> => {
    const res = await PlayerApi.playlist(deviceCode, token);
    if (res.ok) {
      await applyPlaylist(res.data);
      return true;
    }
    if (handleAuthFailure(res.status, res.data)) return false;
    return false;
  }, [deviceCode, applyPlaylist, handleAuthFailure]);

  useEffect(() => {
    if (!deviceCode) return;
    let cancelled = false;

    (async () => {
      // 1. Hydrate from cache so playback can start instantly even
      //    while we wait for the network.
      const cached = await loadCachedPlaylist(deviceCode);
      if (cached && !cancelled) {
        if (cached.playlist_group) {
          setSequence(buildSteps(cached.playlist_group));
          setLayouts(indexLayouts(cached));
          setScreen({ kind: "playing" });
        } else {
          setScreen({ kind: "waiting" });
        }
      }

      // 2. Acquire / refresh token.
      let token = stateRef.current.token;
      if (!token) {
        const reg = await PlayerApi.register(deviceCode, APP_VERSION);
        if (cancelled) return;
        if (reg.ok) {
          token = reg.data.token;
          persist({ token });
        } else if (reg.status === 404) {
          // Device code not provisioned (never created, deleted, or
          // revoked so it's no longer ACTIVE). In the bare /player paired
          // flow this means the stored credential is dead → drop it and
          // return to pairing. On the /player/:deviceCode URL it's almost
          // always a typo → show a dedicated "Invalid Device Code" screen
          // rather than the generic waiting message.
          if (onCredentialInvalidRef.current) {
            setScreen({ kind: "unauthorized" });
            persist({ token: null });
            onCredentialInvalidRef.current();
          } else {
            setScreen({ kind: "invalid_code" });
          }
          return;
        } else {
          // Network or other transient failure — keep using cache.
          if (!cached) setScreen({ kind: "waiting" });
          return;
        }
      }

      // 3. Fetch fresh playlist.
      if (token) await fetchFreshPlaylist(token);

      registerServiceWorker();
    })();

    return () => { cancelled = true; };
  }, [deviceCode, persist, fetchFreshPlaylist]);

  /* ─── Remote-command executor ──────────────────────────────────────── */

  /**
   * Execute a single command. Returns the result envelope ready to be
   * POSTed to /api/device/command-result. Errors are caught so a bad
   * command never crashes the poll loop.
   */
  const executeCommand = useCallback(async (
    cmd: IPlayerCommand,
    token: string,
  ): Promise<{ status: "completed" | "failed"; message: string | null; willRestart?: boolean }> => {
    try {
      switch (cmd.command) {
        case "force_refresh": {
          const ok = await fetchFreshPlaylist(token);
          // Reset cursor (applyPlaylist already does this on swap).
          if (!ok) return { status: "failed", message: "Could not fetch playlist" };
          return { status: "completed", message: "Playlist reloaded" };
        }
        case "clear_cache": {
          await clearMediaCache();
          const ok = await fetchFreshPlaylist(token);
          return ok
            ? { status: "completed", message: "Cache cleared and playlist reloaded" }
            : { status: "failed", message: "Cache cleared but playlist reload failed" };
        }
        case "restart_player": {
          // Report completion BEFORE reloading so the server doesn't
          // see the command stuck in "sent".
          return { status: "completed", message: "Restarting player", willRestart: true };
        }
        case "request_screenshot": {
          // Browser tabs cannot capture their own viewport without an
          // intrusive getDisplayMedia() permission prompt. Honor the
          // spec by reporting failure with a clear message.
          return {
            status: "failed",
            message: "Screenshot not supported in browser player",
          };
        }
        case "install_update": {
          // Web Player has no APK to install — a hard reload picks up
          // whatever bundle the server is now shipping. We report
          // completion before reloading so the row leaves "sent".
          return { status: "completed", message: "Reloading to pick up latest bundle", willRestart: true };
        }
        default:
          return { status: "failed", message: `Unknown command: ${cmd.command}` };
      }
    } catch (e: any) {
      return { status: "failed", message: e?.message || "Unhandled exception" };
    }
  }, [fetchFreshPlaylist]);

  const handleCommands = useCallback(async (
    commands: IPlayerCommand[] | undefined,
    token: string,
  ): Promise<void> => {
    if (!commands || commands.length === 0) return;
    // Process serially so e.g. clear_cache + force_refresh in the same
    // batch don't race each other. One failure does not abort the rest.
    let restartAfter = false;
    for (const cmd of commands) {
      const res = await executeCommand(cmd, token);
      const last: IPlayerLastCommand = {
        id: cmd.id,
        command: cmd.command,
        status: res.status,
        message: res.message,
        at: new Date().toISOString(),
      };
      persist({ last_command: last });
      // Report back to the server.
      await PlayerApi.commandResult(token, cmd.id, res.status, res.message ?? undefined);
      if (res.willRestart) restartAfter = true;
    }
    if (restartAfter) {
      // Defer slightly so the last result POST flushes and the user
      // sees the heartbeat tick complete before the page reloads.
      window.setTimeout(() => window.location.reload(), 250);
    }
  }, [executeCommand, persist]);

  /* ─── 60s poll loop ────────────────────────────────────────────────── */

  useEffect(() => {
    if (!deviceCode) return;
    const tick = async () => {
      const token = stateRef.current.token;
      if (!token) return;

      const cu = await PlayerApi.checkUpdate(deviceCode, token);
      if (cu.ok) {
        const versionChanged = cu.data.version !== stateRef.current.playlist_version;
        if (cu.data.force_refresh || versionChanged) {
          await fetchFreshPlaylist(token);
        }
      } else {
        handleAuthFailure(cu.status, cu.data);
      }
      // Network errors fall through silently — keep playing cache.

      // Heartbeat. The response may include commands[]; if so, run
      // them and POST results back. Backward-compatible: missing array
      // is the same as an empty one. Metadata is recomputed every tick so
      // a display/resolution/orientation change is reconciled on the next
      // heartbeat (no separate polling — see Phase 10); the server only
      // writes it when a value actually changes.
      const hb = await PlayerApi.heartbeat(
        deviceCode,
        token,
        stateRef.current.playlist_version,
        APP_VERSION,
        toHeartbeatMeta(detectDeviceMeta()),
      );
      if (hb.ok) {
        if (hb.data.refresh_required) {
          await fetchFreshPlaylist(token);
        }
        await handleCommands(hb.data.commands, token);
      } else {
        handleAuthFailure(hb.status, hb.data);
      }
    };

    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, [deviceCode, fetchFreshPlaylist, handleCommands, handleAuthFailure]);

  /* ─── Online / offline tracking ────────────────────────────────────── */

  useEffect(() => {
    const on = () => {
      setOnline(true);
      const token = stateRef.current.token;
      if (token) void PlayerApi.event(token, "network_restored");
    };
    const off = () => {
      setOnline(false);
      const token = stateRef.current.token;
      if (token) void PlayerApi.event(token, "network_lost");
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  /* ─── Advance cursor ───────────────────────────────────────────────── */

  // Watchdog: stamp each cursor change so the no-progress watchdog
  // below can detect stalled playback. Kept on a ref to avoid forcing
  // re-renders.
  const lastProgressMsRef = useRef<number>(Date.now());

  const advance = useCallback(() => {
    setCurrentIndex((i) => {
      const seq = sequenceRef.current;
      if (seq.length === 0) return 0;
      return (i + 1) % seq.length;
    });
    lastProgressMsRef.current = Date.now();
  }, []);

  // Player-started breadcrumb (once per page-load).
  useEffect(() => {
    if (!deviceCode) return;
    // Defer past initial boot so we know the token has been resolved.
    const id = window.setTimeout(() => {
      const token = stateRef.current.token;
      if (token) {
        void PlayerApi.event(token, "player_started", "Web Player started", {
          user_agent: navigator.userAgent.slice(0, 200),
          build: APP_VERSION,
        });
      }
    }, 4_000);
    return () => window.clearTimeout(id);
  }, [deviceCode]);

  // Per-media start/complete events. Fires when the cursor changes or
  // a new sequence is loaded. media_failed is reported by the
  // ImageItem / VideoItem onError handlers (wired below via advance).
  const lastReportedIndexRef = useRef<number>(-1);
  useEffect(() => {
    if (screen.kind !== "playing") return;
    const step = sequence[currentIndex];
    if (!step) return;
    if (lastReportedIndexRef.current === currentIndex) return;
    lastReportedIndexRef.current = currentIndex;
    const token = stateRef.current.token;
    if (!token) return;
    if (step.kind === "template") {
      void PlayerApi.event(token, "template_started",
        `Playing template ${step.templateId}`,
        { template_id: step.templateId, cursor: currentIndex });
    } else {
      const m = step.media;
      void PlayerApi.event(token, "media_started", `Playing ${m.url.split("/").pop()}`, {
        media_id: m.id,
        media_type: m.type,
        cursor: currentIndex,
      });
    }
  }, [sequence, currentIndex, screen.kind]);

  /* ─── Image watchdog: force-advance if image timer was lost ───────── */

  useEffect(() => {
    if (screen.kind !== "playing") return;
    const step = sequence[currentIndex];
    if (!step) return;
    // Watchdog applies to anything with a known duration: image steps
    // (V1) and template steps (V2). Videos drive their own onEnded.
    const isMediaImage = step.kind === "media" && step.media.type === "image";
    const isTemplate = step.kind === "template";
    if (!isMediaImage && !isTemplate) return;
    const buffer = stepDurationMs(step) + 30_000;
    const id = window.setTimeout(advance, buffer);
    return () => window.clearTimeout(id);
  }, [sequence, currentIndex, screen.kind, advance]);

  /* ─── Image / template auto-advance timer ──────────────────────────── */

  // Spec §8: the item is active for its full configured duration; the
  // transition INTO the next item plays during this item's final window so
  // the next item is fully active exactly at the duration boundary — the
  // transition is never added on top of the item's time.
  useEffect(() => {
    if (screen.kind !== "playing") return;
    const step = sequence[currentIndex];
    if (!step) return;
    const isMediaImage = step.kind === "media" && step.media.type === "image";
    const isTemplate = step.kind === "template";
    if (!isMediaImage && !isTemplate) return;
    const dwell = stepDurationMs(step);
    const thisEff = effectiveTransition(step);
    const tIn = effectiveTransitionMs(thisEff.id, thisEff.durationMs, dwell);
    const nextStep = sequence.length ? sequence[(currentIndex + 1) % sequence.length] : undefined;
    const nextEff = effectiveTransition(nextStep);
    const tNext = effectiveTransitionMs(nextEff.id, nextEff.durationMs, dwell);
    const id = window.setTimeout(advance, sceneSwitchDelayMs(dwell, tIn, tNext));
    return () => window.clearTimeout(id);
  }, [sequence, currentIndex, screen.kind, advance]);

  /* ─── No-progress watchdog (5 min) ─────────────────────────────────── */

  // If the cursor hasn't advanced AND we believe we're playing, the
  // most likely cause is a stuck <video> or a dropped image timer.
  // Hard-reload the tab — cheaper than diagnosing the freeze.
  useEffect(() => {
    if (screen.kind !== "playing") {
      lastProgressMsRef.current = Date.now();
      return;
    }
    const id = window.setInterval(() => {
      const stalledMs = Date.now() - lastProgressMsRef.current;
      if (stalledMs >= 5 * 60 * 1000) {
        const token = stateRef.current.token;
        if (token) {
          void PlayerApi.event(token, "player_recovered",
            "No playback progress for 5m — auto-reload", { stalled_ms: stalledMs });
        }
        // Give the event POST a moment to flush, then reload.
        window.setTimeout(() => window.location.reload(), 500);
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [screen.kind]);

  /* ─── Nightly reload (3 AM) to release accumulated leaks ──────────── */

  // Long-running browser tabs leak DOM nodes / GPU surfaces / GC
  // generations regardless of how careful the player is. A nightly
  // hard reload is the simplest backstop. Idempotent — only fires
  // once per local day.
  useEffect(() => {
    const stampKey = "vmd_player_last_nightly_reload";
    const id = window.setInterval(() => {
      const now = new Date();
      // Reload window: 03:00–03:05 local time. Generous so we don't
      // miss the hour if the heartbeat tick happens to skip.
      if (now.getHours() !== 3 || now.getMinutes() > 5) return;
      const today = now.toISOString().slice(0, 10);
      const last = localStorage.getItem(stampKey);
      if (last === today) return;
      localStorage.setItem(stampKey, today);
      const token = stateRef.current.token;
      if (token) void PlayerApi.event(token, "player_recovered",
        "Nightly 3am reload");
      window.setTimeout(() => window.location.reload(), 500);
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  /* ─── Cursor hide ──────────────────────────────────────────────────── */

  useEffect(() => {
    let id: number | undefined;
    const onMove = () => {
      setCursorVisible(true);
      if (id) window.clearTimeout(id);
      id = window.setTimeout(() => setCursorVisible(false), CURSOR_HIDE_MS);
    };
    onMove();
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (id) window.clearTimeout(id);
    };
  }, []);

  /* ─── Diagnostics — D pressed 5 times within 3s ───────────────────── */

  useEffect(() => {
    let presses: number[] = [];
    const openDiagnostics = async () => {
      const cache = await estimateCacheSize();
      const s = stateRef.current;
      const step = sequenceRef.current[indexRef.current];
      const lc = s.last_command;
      const currentLabel = !step
        ? "—"
        : step.kind === "media"
          ? `${step.media.type}: ${step.media.url.split("/").pop()}`
          : `template: #${step.templateId}`;
      setDiagInfo({
        device_code: deviceCode,
        device_name: s.cached_playlist?.device?.device_name ?? null,
        playlist_version: s.playlist_version,
        online: navigator.onLine,
        cache_bytes: cache.bytes,
        cache_count: cache.count,
        last_sync: s.last_sync,
        current_media: currentLabel,
        api_url: (process.env.REACT_APP_API_URL || "").trim(),
        app_version: APP_VERSION,
        last_command: lc ? `${lc.command} (#${lc.id})` : null,
        last_command_result: lc ? `${lc.status}${lc.message ? ` — ${lc.message}` : ""}` : null,
      });
      setShowDiagnostics(true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowDiagnostics(false);
        return;
      }
      if (e.key === "d" || e.key === "D") {
        const now = Date.now();
        presses = [...presses.filter(t => now - t < 3000), now];
        if (presses.length >= 5) {
          presses = [];
          void openDiagnostics();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deviceCode]);

  /* ─── Render ───────────────────────────────────────────────────────── */

  const containerStyle: React.CSSProperties = useMemo(() => ({
    cursor: cursorVisible ? "default" : "none",
  }), [cursorVisible]);

  if (screen.kind === "blocked") {
    const { title, subtitle } = reasonScreen(screen.reason);
    return (
      <div className="vmd-player" style={containerStyle}>
        <div className="vmd-player-center">
          <div className="vmd-player-title">{title}</div>
          {subtitle && (
            <div className="vmd-player-muted" style={{ marginTop: 24 }}>{subtitle}</div>
          )}
        </div>
      </div>
    );
  }

  if (screen.kind === "unauthorized") {
    return (
      <div className="vmd-player" style={containerStyle}>
        <div className="vmd-player-center">
          <div className="vmd-player-title">Device Not Authorized</div>
          <div className="vmd-player-muted" style={{ marginTop: 24 }}>
            This screen has been disconnected. Re-pair it from VM Display.
          </div>
        </div>
      </div>
    );
  }

  if (screen.kind === "invalid_code") {
    return (
      <div className="vmd-player" style={containerStyle}>
        <div className="vmd-player-center">
          <div className="vmd-player-title">Invalid Device Code</div>
          <div className="vmd-player-subtitle" style={{ marginTop: 24 }}>
            <code style={{ background: "rgba(255,255,255,0.06)", padding: "4px 12px",
              borderRadius: 6, fontSize: "0.7em" }}>
              {deviceCode || "(missing)"}
            </code>
          </div>
          <div className="vmd-player-muted" style={{ marginTop: 24 }}>
            Please check the code in the VM Display admin panel.
          </div>
        </div>
      </div>
    );
  }

  if (screen.kind === "waiting" || (screen.kind === "playing" && sequence.length === 0)) {
    return (
      <div className="vmd-player" style={containerStyle}>
        <div className="vmd-player-center">
          <div className="vmd-player-brand">VM DISPLAY</div>
          <div className="vmd-player-subtitle">Device Registered</div>
          <div className="vmd-player-muted">Waiting for content…</div>
          {!online && (
            <div className="vmd-player-offline">offline · cached content unavailable</div>
          )}
        </div>
        {showDiagnostics && diagInfo && (
          <Diagnostics info={diagInfo} onClose={() => setShowDiagnostics(false)} />
        )}
      </div>
    );
  }

  if (screen.kind === "boot") {
    return (
      <div className="vmd-player" style={containerStyle}>
        <div className="vmd-player-center">
          <div className="vmd-player-brand">VM DISPLAY</div>
          <div className="vmd-player-muted">Starting…</div>
        </div>
      </div>
    );
  }

  const step = sequence[currentIndex];

  // A missing template layout must not stall — skip on the next tick.
  if (step && step.kind === "template" && !layouts[step.templateId]) {
    window.setTimeout(advance, 0);
  }

  // Effective transition for entering this step (playlist boundary wins
  // over the item's own transition). `involvesVideo` downgrades unsafe
  // effects to a crossfade when either the incoming or outgoing scene is
  // a video (the <video> surface fights clip-path / 3D transforms).
  const prevStep = sequence.length ? sequence[(currentIndex - 1 + sequence.length) % sequence.length] : undefined;
  const isVideo = (s?: PlayerStep) => !!s && s.kind === "media" && s.media.type === "video";
  const involvesVideo = isVideo(step) || isVideo(prevStep);
  const eff = effectiveTransition(step);
  const plan = buildTransitionPlan(eff.id, eff.easing, eff.durationMs, { involvesVideo });

  // Transition into the NEXT step — used to preempt a video so its outgoing
  // transition finishes at its natural end (spec §8), matching the editor
  // Preview and the image/template timer above.
  const nextStepForVideo = sequence.length ? sequence[(currentIndex + 1) % sequence.length] : undefined;
  const nextEffForVideo = effectiveTransition(nextStepForVideo);
  const videoNearEndMs = effectiveTransitionMs(nextEffForVideo.id, nextEffForVideo.durationMs, Number.MAX_SAFE_INTEGER);

  // Guarded event handlers — only the active scene (matching indexRef) may
  // fire advance, so a still-playing OUTGOING video can't double-advance.
  const makeHandlers = (idx: number, media: IPlayerMedia) => ({
    onEnded: () => {
      if (indexRef.current !== idx) return;
      const token = stateRef.current.token;
      if (token) void PlayerApi.event(token, "media_completed",
        `Video done: ${media.url.split("/").pop()}`, { media_id: media.id, media_type: media.type });
      advance();
    },
    onError: () => {
      if (indexRef.current !== idx) return;
      const token = stateRef.current.token;
      if (token) void PlayerApi.event(token, "media_failed",
        `Media failed: ${media.url.split("/").pop()}`, { media_id: media.id, media_type: media.type });
      advance();
    },
    onNearEnd: () => {
      if (indexRef.current !== idx) return;
      advance();
    },
  });

  let scene: React.ReactNode = <div style={{ width: "100%", height: "100%", background: "#000" }} />;
  if (step && step.kind === "template") {
    const layout = layouts[step.templateId];
    if (layout) scene = <LayoutRenderer layout={layout} />;
  } else if (step && step.kind === "media") {
    const h = makeHandlers(currentIndex, step.media);
    scene = (
      <MediaScene
        media={step.media} onEnded={h.onEnded} onError={h.onError}
        nearEndMs={step.media.type === "video" ? videoNearEndMs : 0}
        onNearEnd={h.onNearEnd}
      />
    );
  }

  return (
    <div className="vmd-player" style={containerStyle}>
      {step && (
        <TransitionStage sceneKey={currentIndex} plan={plan} className="vmd-player-media">
          {scene}
        </TransitionStage>
      )}
      {showDiagnostics && diagInfo && (
        <Diagnostics info={diagInfo} onClose={() => setShowDiagnostics(false)} />
      )}
    </div>
  );
};

export default Player;
